import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, Pressable } from 'react-native';
import { useMutation } from '@tanstack/react-query';
import { ClipboardList, User, Home, Building, MapPin, Map, Mail, Check } from 'lucide-react';
import { webColors, webBorderRadius, webShadows } from '../../../../constants/webStyles';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import { getAdministrativeDivisionLabel } from '../../../../constants/countries';
import { validateFields, requestOTP } from '../../../../services/auth';

const STATES_BY_COUNTRY = {
  MY: ['Johor', 'Kedah', 'Kelantan', 'Kuala Lumpur', 'Melaka', 'Negeri Sembilan', 'Pahang', 'Penang', 'Perak', 'Perlis', 'Sabah', 'Sarawak', 'Selangor', 'Terengganu', 'Labuan', 'Putrajaya'],
  SG: ['Central Region', 'East Region', 'North Region', 'North-East Region', 'West Region'],
  PH: ['Metro Manila', 'Cebu', 'Davao', 'Calabarzon', 'Central Luzon', 'Western Visayas', 'Central Visayas', 'Ilocos Region', 'Bicol Region', 'Eastern Visayas'],
  ID: ['Jakarta', 'West Java', 'Central Java', 'East Java', 'Banten', 'Bali', 'North Sumatra', 'South Sulawesi', 'East Kalimantan', 'Yogyakarta'],
};

const StepDetailsWeb = ({ formData, updateFormData, goToStep, isDoctor }) => {
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const isMobile = useIsMobile();

  const states = useMemo(() => {
    return STATES_BY_COUNTRY[formData.country] || STATES_BY_COUNTRY.MY;
  }, [formData.country]);

  const administrativeDivisionLabel = getAdministrativeDivisionLabel(formData.country);

  // Validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate fields with API
  const validateMutation = useMutation(
    (data) => validateFields(data),
    {
      onError: (err) => {
        const data = err.response?.data;
        if (data?.errors) {
          const messages = Object.values(data.errors).flat();
          setError(messages.join('\n'));
        } else {
          setError(data?.message || 'Validation failed');
        }
      },
      onSuccess: (res) => {
        // Validation passed, request OTP
        requestOTPMutation.mutate({ email: formData.email });
      },
    }
  );

  // Request OTP
  const requestOTPMutation = useMutation(
    (data) => requestOTP(data),
    {
      onError: (err) => {
        const data = err.response?.data;
        if (data?.errors) {
          const messages = Object.values(data.errors).flat();
          setError(messages.join('\n'));
        } else {
          setError(data?.message || 'Failed to send verification code');
        }
      },
      onSuccess: (res) => {
        // Store nonce for OTP verification
        updateFormData('nonce', res.data.data.nonce);
        // OTP sent successfully, go to next step
        goToStep(4);
      },
    }
  );

  const isValid = formData.fullName && formData.email && isValidEmail(formData.email) && formData.termsAccepted;
  const isLoading = validateMutation.isLoading || requestOTPMutation.isLoading;

  const handleSubmit = () => {
    // Clear previous errors
    setError('');
    setFieldErrors({});

    // Client-side validation
    const errors = {};
    if (!formData.fullName) {
      errors.fullName = 'Full name is required';
    }
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!isValidEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!formData.termsAccepted) {
      setError('Please accept the Terms & Conditions');
      return;
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    // Validate with API
    validateMutation.mutate({
      email: formData.email,
      scope: isDoctor ? 'doctor' : 'clinic_owner',
    });
  };

  return (
    <View style={styles.container}>
      <View style={styles.stepHeader}>
        <View style={styles.stepIcon}>
          <ClipboardList size={48} color={webColors.primary} />
        </View>
        <Text style={styles.stepTitle}>Account Credentials</Text>
        <Text style={styles.stepSubtitle}>How can we reach you?</Text>
      </View>

      {error ? (
        <View style={styles.errorBox}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      ) : null}

      <View style={styles.form}>
        {/* Full Name */}
        <View style={styles.formGroup}>
          <Text style={styles.formLabel}>Full Name *</Text>
          <View style={styles.inputWrapper}>
            <View style={styles.inputIcon}>
              <User size={18} color={webColors.textMuted} />
            </View>
            <TextInput
              style={[styles.input, fieldErrors.fullName && styles.inputError]}
              placeholder="Enter your full name"
              placeholderTextColor={webColors.textMuted}
              value={formData.fullName}
              onChangeText={(value) => {
                updateFormData('fullName', value);
                setFieldErrors(prev => ({ ...prev, fullName: null }));
              }}
              editable={!isLoading}
            />
          </View>
          {fieldErrors.fullName && (
            <Text style={styles.fieldError}>{fieldErrors.fullName}</Text>
          )}
        </View>

        {/* Home Address */}
        <View style={styles.formGroup}>
          <Text style={styles.formLabel}>Home Address</Text>
          <View style={styles.inputWrapper}>
            <View style={styles.inputIcon}>
              <Home size={18} color={webColors.textMuted} />
            </View>
            <TextInput
              style={styles.input}
              placeholder="Street address"
              placeholderTextColor={webColors.textMuted}
              value={formData.address}
              onChangeText={(value) => updateFormData('address', value)}
              editable={!isLoading}
            />
          </View>
        </View>

        {/* City / Town */}
        <View style={styles.formGroup}>
          <Text style={styles.formLabel}>City / Town</Text>
          <View style={styles.inputWrapper}>
            <View style={styles.inputIcon}>
              <Building size={18} color={webColors.textMuted} />
            </View>
            <TextInput
              style={styles.input}
              placeholder="Enter city or town"
              placeholderTextColor={webColors.textMuted}
              value={formData.city}
              onChangeText={(value) => updateFormData('city', value)}
              editable={!isLoading}
            />
          </View>
        </View>

        {/* Postal Code & State */}
        <View style={[styles.formRow, isMobile && styles.formRowMobile]}>
          <View style={[styles.formGroup, { flex: 1 }]}>
            <Text style={styles.formLabel}>Postal Code</Text>
            <View style={styles.inputWrapper}>
              <View style={styles.inputIcon}>
                <MapPin size={18} color={webColors.textMuted} />
              </View>
              <TextInput
                style={styles.input}
                placeholder="Postal code"
                placeholderTextColor={webColors.textMuted}
                value={formData.postalCode}
                onChangeText={(value) => updateFormData('postalCode', value)}
                editable={!isLoading}
              />
            </View>
          </View>
          <View style={[styles.formGroup, { flex: 1 }]}>
            <Text style={styles.formLabel}>{administrativeDivisionLabel}</Text>
            <View style={styles.inputWrapper}>
              <View style={styles.inputIcon}>
                <Map size={18} color={webColors.textMuted} />
              </View>
              <TextInput
                style={styles.input}
                placeholder={`Select ${administrativeDivisionLabel.toLowerCase()}`}
                placeholderTextColor={webColors.textMuted}
                value={formData.state}
                onChangeText={(value) => updateFormData('state', value)}
                editable={!isLoading}
              />
            </View>
          </View>
        </View>

        {/* Email Address */}
        <View style={styles.formGroup}>
          <Text style={styles.formLabel}>Email Address *</Text>
          <View style={styles.inputWrapper}>
            <View style={styles.inputIcon}>
              <Mail size={18} color={webColors.textMuted} />
            </View>
            <TextInput
              style={[styles.input, fieldErrors.email && styles.inputError]}
              placeholder="Enter your email"
              placeholderTextColor={webColors.textMuted}
              value={formData.email}
              onChangeText={(value) => {
                updateFormData('email', value);
                setFieldErrors(prev => ({ ...prev, email: null }));
              }}
              keyboardType="email-address"
              autoCapitalize="none"
              editable={!isLoading}
            />
          </View>
          {fieldErrors.email && (
            <Text style={styles.fieldError}>{fieldErrors.email}</Text>
          )}
        </View>

        {/* Terms & Conditions */}
        <Pressable
          style={styles.termsCheck}
          onPress={() => updateFormData('termsAccepted', !formData.termsAccepted)}
          disabled={isLoading}
        >
          <View style={[
            styles.checkbox,
            formData.termsAccepted && styles.checkboxChecked,
          ]}>
            {formData.termsAccepted && <Check size={14} color="#ffffff" />}
          </View>
          <Text style={styles.termsText}>
            I have read and agreed to the{' '}
            <Text style={styles.termsLink}>Terms & Conditions</Text>
          </Text>
        </Pressable>
      </View>

      <View style={styles.btnRow}>
        <TouchableOpacity
          style={styles.btnBack}
          onPress={() => goToStep(2)}
          disabled={isLoading}
        >
          <Text style={styles.btnBackText}>Back</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.btnNext, (!isValid || isLoading) && styles.btnNextDisabled]}
          onPress={handleSubmit}
          disabled={!isValid || isLoading}
        >
          <Text style={[styles.btnNextText, (!isValid || isLoading) && styles.btnNextTextDisabled]}>
            {isLoading ? 'Submitting...' : 'Submit'}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  stepHeader: {
    marginBottom: 32,
  },
  stepIcon: {
    marginBottom: 16,
  },
  stepTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  stepSubtitle: {
    fontSize: 16,
    color: webColors.primary,
  },
  errorBox: {
    backgroundColor: webColors.errorLight,
    padding: 12,
    borderRadius: webBorderRadius.small,
    marginBottom: 16,
  },
  errorText: {
    color: webColors.error,
    fontSize: 14,
  },
  form: {
    marginBottom: 24,
  },
  formGroup: {
    marginBottom: 16,
  },
  formRow: {
    flexDirection: 'row',
    gap: 16,
  },
  formRowMobile: {
    flexDirection: 'column',
    gap: 16,
  },
  formLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textSecondary,
    marginBottom: 6,
  },
  inputWrapper: {
    position: 'relative',
  },
  inputIcon: {
    position: 'absolute',
    left: 14,
    top: '50%',
    transform: [{ translateY: -9 }],
    zIndex: 1,
  },
  input: {
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 2,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    paddingVertical: 14,
    paddingLeft: 44,
    paddingRight: 16,
    fontSize: 15,
    color: webColors.textPrimary,
  },
  inputError: {
    borderColor: webColors.error,
  },
  fieldError: {
    color: webColors.error,
    fontSize: 12,
    marginTop: 4,
  },
  termsCheck: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginTop: 24,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: webColors.border,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 2,
  },
  checkboxChecked: {
    backgroundColor: webColors.primary,
    borderColor: webColors.primary,
  },
  termsText: {
    fontSize: 14,
    color: webColors.textSecondary,
    lineHeight: 22,
    flex: 1,
  },
  termsLink: {
    color: webColors.secondaryLight,
    textDecorationLine: 'none',
  },
  btnRow: {
    flexDirection: 'row',
    gap: 16,
  },
  btnBack: {
    flex: 1,
    backgroundColor: webColors.backgroundAlt,
    paddingVertical: 16,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
  },
  btnBackText: {
    color: webColors.textSecondary,
    fontSize: 16,
    fontWeight: '600',
  },
  btnNext: {
    flex: 2,
    backgroundColor: webColors.primary,
    paddingVertical: 16,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
    ...webShadows.button,
  },
  btnNextDisabled: {
    backgroundColor: webColors.border,
    shadowOpacity: 0,
  },
  btnNextText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  btnNextTextDisabled: {
    color: webColors.textMuted,
  },
});

export default StepDetailsWeb;
